import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";

/**
 * Summarizes unified-ehr timeline into brief counts by type for a ticket.
 */
function summarizeTimeline(timeline) {
  if (!timeline?.length) return {};
  const counts = {};
  timeline.forEach((item) => {
    const type = item.type || "other";
    counts[type] = (counts[type] || 0) + 1;
  });
  return counts;
}

const PatientTicketsList = ({ patientId, userProfile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useAuth()?.user?.token;
  const [expandedTicketId, setExpandedTicketId] = useState(null);

  const { data: ticketsData, isLoading: loadingTickets } = useFetchObjects(
    patientId ? ["patient-tickets", patientId] : null,
    patientId ? `tickets/?patient=${patientId}` : "",
    token
  );

  const tickets = ticketsData?.results ?? (Array.isArray(ticketsData) ? ticketsData : []);

  const { data: ehrData, isLoading: loadingEhr } = useFetchObjects(
    patientId && expandedTicketId
      ? ["ticket-ehr-brief", patientId, expandedTicketId]
      : null,
    patientId && expandedTicketId
      ? `patients/${patientId}/unified-ehr/?ticket_id=${expandedTicketId}`
      : "",
    token
  );

  const summary = React.useMemo(
    () => summarizeTimeline(ehrData?.timeline),
    [ehrData?.timeline]
  );

  const handleAccordionChange = (ticketId) => (_, isExpanded) => {
    setExpandedTicketId(isExpanded ? ticketId : null);
  };

  const handleViewFullTicket = (ticket) => {
    navigate(`/patient-profile/ticket/${ticket.id}`, {
      state: { ticket, patient: userProfile },
    });
  };

  if (!patientId) return null;

  if (loadingTickets) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (tickets.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t("profile.noTicketsForPatient") || "No tickets found for this patient."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        {t("profile.allTickets") || "All tickets"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("profile.allTicketsDescription") ||
          "Click a ticket to see a brief summary. Use “View full ticket details” to open that visit."}
      </Typography>
      {tickets.map((ticket) => (
        <Accordion
          key={ticket.id}
          expanded={expandedTicketId === ticket.id}
          onChange={handleAccordionChange(ticket.id)}
          sx={{ "&:before": { display: "none" }, boxShadow: 1, mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography fontWeight="medium">
                {ticket.ticket_number || `#${ticket.id}`}
              </Typography>
              <Chip
                size="small"
                label={ticket.status_display || t("profile.active")}
                color={
                  ticket.status === 1
                    ? "success"
                    : ticket.status === 2
                    ? "default"
                    : "error"
                }
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">
                {ticket.visit_date
                  ? new Date(ticket.visit_date).toLocaleDateString(undefined, {
                      dateStyle: "medium",
                    })
                  : "—"}
              </Typography>
              {ticket.department_name_display && (
                <Chip
                  size="small"
                  label={ticket.department_name_display}
                  variant="outlined"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {expandedTicketId === ticket.id && (
              <>
                {loadingEhr ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t("profile.whatHappenedInThisTicket") ||
                        "What happened in this visit"}
                    </Typography>
                    {Object.keys(summary).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.noActivityRecorded") || "No activity recorded for this ticket."}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        {summary.encounter > 0 && (
                          <Chip
                            size="small"
                            label={`${t("profile.encounters")}: ${summary.encounter}`}
                            variant="outlined"
                          />
                        )}
                        {summary.prescription > 0 && (
                          <Chip
                            size="small"
                            label={`${t("profile.prescriptions")}: ${summary.prescription}`}
                            variant="outlined"
                          />
                        )}
                        {(summary.laboratory > 0 || summary.lab_order > 0) && (
                          <Chip
                            size="small"
                            label={`${t("profile.labResults")}: ${summary.laboratory || summary.lab_order}`}
                            variant="outlined"
                          />
                        )}
                        {summary.xray > 0 && (
                          <Chip
                            size="small"
                            label={`${t("profile.xray")}: ${summary.xray}`}
                            variant="outlined"
                          />
                        )}
                        {summary.ultrasound > 0 && (
                          <Chip
                            size="small"
                            label={`${t("profile.ultrasound")}: ${summary.ultrasound}`}
                            variant="outlined"
                          />
                        )}
                        {summary.echo > 0 && (
                          <Chip
                            size="small"
                            label={`${t("profile.echo")}: ${summary.echo}`}
                            variant="outlined"
                          />
                        )}
                        {summary.vital_signs > 0 && (
                          <Chip
                            size="small"
                            label={`${t("profile.vitalSigns")}: ${summary.vital_signs}`}
                            variant="outlined"
                          />
                        )}
                        {(summary.nursing_note > 0 || summary.clinical_note > 0) && (
                          <Chip
                            size="small"
                            label={`${t("profile.notes")}: ${summary.nursing_note || summary.clinical_note}`}
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleViewFullTicket(ticket)}
                    >
                      {t("profile.viewFullTicketDetails") || "View full ticket details"}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default PatientTicketsList;
